import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import UrgentBanner from "@/components/UrgentBanner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, LogOut, User, ChevronDown } from "lucide-react";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    return (
        <div className="min-h-screen bg-background text-foreground flex">
            {/* Desktop sidebar */}
            <aside className="hidden md:block w-64 fixed inset-y-0 left-0 border-r border-border bg-background">
                <Sidebar />
            </aside>

            <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
                {/* Topbar */}
                <header className="h-14 md:h-16 sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border flex items-center justify-between px-4 md:px-6">
                    <div className="flex items-center gap-2">
                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="md:hidden" data-testid="open-sidebar">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="p-0 w-72">
                                <Sidebar onNavigate={() => setOpen(false)} />
                            </SheetContent>
                        </Sheet>
                        <p className="font-heading font-bold tracking-tight text-base md:text-lg flex items-center gap-2">
                            <img src="/foresta-icon.png" alt="" className="h-7 w-7 md:hidden object-contain" />
                            <span className="md:hidden text-primary">Foresta</span>
                            <span className="hidden md:inline">Foresta — Reservas y Reclamos</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                                    <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-semibold">
                                        {(user?.nombre || "?").slice(0, 2).toUpperCase()}
                                    </span>
                                    <span className="hidden sm:flex flex-col items-start leading-tight">
                                        <span className="text-sm font-medium">{user?.nombre}</span>
                                        <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{user?.rol}</span>
                                    </span>
                                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-muted-foreground">{user?.email}</span>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/mi-perfil")} data-testid="mi-perfil-menu">
                                    <User className="h-4 w-4 mr-2" /> Mi perfil
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                                    <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <UrgentBanner />

                <main className="flex-1 p-4 md:p-8 animate-fade-in">{children}</main>
            </div>
        </div>
    );
}
